import React, { useMemo } from "react";
import { Crown } from "@phosphor-icons/react";

// Sprint 68 — who wore the crown across a showdown portfolio, with the
// forced CPT MIN floor drawn as a tick so forced captains are visible at a glance.
export const CaptainShareChart = ({ lineups = [], overrides = {}, cptMinPct = {} }) => {
  const rows = useMemo(() => {
    const total = lineups.length;
    if (!total) return [];
    const counts = new Map();
    for (const L of lineups) {
      const cpt = (L.players || []).find((p) => p.roster_slot === "CPT") || (L.players || [])[0];
      if (!cpt) continue;
      const id = String(cpt.dk_id);
      const cur = counts.get(id) || { id, name: cpt.name, team: cpt.team, position: cpt.position, n: 0 };
      cur.n += 1;
      counts.set(id, cur);
    }
    return [...counts.values()]
      .map((r) => {
        const floor = Number((overrides[r.id] || {}).cpt_min_exposure_pct ?? cptMinPct[r.id] ?? 0) || 0;
        return { ...r, pct: (100 * r.n) / total, floor, met: floor === 0 || (100 * r.n) / total >= floor - 1e-6 };
      })
      .sort((a, b) => b.n - a.n);
  }, [lineups, overrides, cptMinPct]);

  if (!rows.length) return null;
  const max = Math.max(...rows.map((r) => r.pct), 1);
  return (
    <section data-testid="captain-share-chart" className="border border-zinc-800 bg-[#0b0b0b] p-4">
      <div className="flex items-center gap-2 mb-3">
        <Crown size={14} weight="fill" className="text-[#FF3B30]" />
        <h3 className="mono text-[10px] uppercase tracking-widest text-zinc-400">Captain share · {lineups.length} lineups</h3>
        <span className="mono text-[9px] text-zinc-600 ml-auto">▲ = your CPT MIN floor</span>
      </div>
      <ul className="space-y-1.5">
        {rows.map((r) => (
          <li key={r.id} data-testid={`captain-share-${r.id}`} className="grid grid-cols-[150px_1fr_58px] items-center gap-3">
            <div className="truncate">
              <span className="text-xs text-white">{r.name}</span>
              <span className="mono text-[9px] text-zinc-500 ml-1">{r.position} · {r.team}</span>
            </div>
            <div className="relative h-4 bg-zinc-900 border border-zinc-800">
              <div
                className={`h-full transition-[width] duration-500 ${r.floor ? (r.met ? "bg-[#FF3B30]" : "bg-[#FFCC00]") : "bg-[#39FF14]/70"}`}
                style={{ width: `${(100 * r.pct) / max}%` }}
              />
              {r.floor > 0 && (
                <span
                  title={`CPT MIN ${r.floor}%`}
                  className="absolute -bottom-1.5 text-[8px] text-white leading-none"
                  style={{ left: `calc(${(100 * Math.min(r.floor, max)) / max}% - 4px)` }}
                >▲</span>
              )}
            </div>
            <div className="mono text-[10px] text-right">
              <span className={r.floor && !r.met ? "text-[#FFCC00]" : "text-zinc-200"}>{r.pct.toFixed(0)}%</span>
              <span className="text-zinc-600"> · {r.n}</span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default CaptainShareChart;
